import React, { useRef, useState, useEffect, FocusEvent } from 'react';
import Header from './Header';
import CanvasControls from './CanvasControls';
import StageEditor from './StageEditor';
import JourneyProfileSection from './JourneyProfileSection';
import JourneyContent from './JourneyContent';
import InsightsSection from './InsightsSection';
import { useJourneyMap } from '@/hooks/useJourneyMap';
import { 
  exportJourneyAsPNG, 
  exportJourneyAsPDF, 
  exportJourneyAsJSON, 
  importJourneyMap 
} from '@/utils/export';
import { UserJourney } from '@/types/journey';
import { calculateCompletionPercentage } from '@/utils/completionUtils';
import { saveCurrentJourney } from '@/utils/storage';
const JourneyMap: React.FC = () => {
  const {
    stages,
    journey,
    setStages,
    setJourney,
    editingStage,
    zoomLevel,
    setZoomLevel,
    setEditingStage,
    handleAddStage,
    handleUpdateStage,
    handleDeleteStage,
    handleEmotionChange,
    handleQuoteChange,
    handleDescriptionChange,
    handlePainPointsChange,
    handleOpportunitiesChange,
    handleTitleChange,
    handleContentUpdate,
    handleProfileUpdate,
    handleMapNameChange,
    handleZoomIn,
    handleZoomOut,
    handleReset,
    handleClearAll,
    isMobile
  } = useJourneyMap();
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const insightsSectionRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isStagesVisible, setIsStagesVisible] = useState(true);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  
  // Handle scroll events with debounced detection
  useEffect(() => {
    // Use a simpler scroll detection with a debounce mechanism
    let scrollTimeout: NodeJS.Timeout | null = null;
    
    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear previous timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Set a timeout to detect when scrolling stops
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
        
        // Simple visibility checks
        if (mainRef.current) {
          // Check journey content visibility
          const journeyContent = mainRef.current.querySelector('.journey-content');
          if (journeyContent) {
            const rect = journeyContent.getBoundingClientRect();
            setIsStagesVisible(rect.top < window.innerHeight && rect.bottom > 0);
          }
          
          // Check insights section visibility
          if (insightsSectionRef.current) {
            const rect = insightsSectionRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;
            const documentHeight = document.documentElement.scrollHeight;
            
            // Simplified check: insights section is visible and we're close to bottom
            const isVisible = rect.top < windowHeight && rect.bottom > 0;
            const isNearBottom = (windowHeight + scrollPosition) >= (documentHeight - 100);
            
            setIsAtBottom(isVisible && isNearBottom);
          }
        }
      }, 200);
    };
    
    // Add scroll listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    if (mainRef.current) {
      mainRef.current.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    // Initial check
    handleScroll();
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainRef.current) {
        mainRef.current.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);
  
  // Simplified focus/blur handling
  useEffect(() => {
    const handleFocus = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        setIsInputFocused(true);
        
        // Immediately hide controls when focusing on insights section inputs
        if (insightsSectionRef.current?.contains(target)) {
          setIsAtBottom(false);
        }
      }
    };
    
    const handleBlur = (e: Event) => {
      const target = e.target as HTMLElement;
      const focusEvent = e as unknown as FocusEvent;
      const relatedTarget = focusEvent.relatedTarget as HTMLElement | null;
      
      // Only set not focused if we're not moving to another input/textarea
      if ((target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') && 
          (!relatedTarget || (relatedTarget.tagName !== 'INPUT' && relatedTarget.tagName !== 'TEXTAREA'))) {
        
        // Add a small delay before updating the focus state
        // This helps prevent UI flashing when focus is moving between inputs
        setTimeout(() => {
          // Double-check that no input is focused
          const activeElement = document.activeElement;
          if (!activeElement || 
              (activeElement.tagName !== 'INPUT' && 
               activeElement.tagName !== 'TEXTAREA')) {
            setIsInputFocused(false);
          }
        }, 50);
      }
    };
    
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    
    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);
  
  const handleExport = () => {
    exportJourneyAsPNG(canvasRef, zoomLevel, setZoomLevel, journey.mapName || 'Journey Map');
  };

  const handleExportPDF = () => {
    // Pass the mapName safely, ensuring it has a default value if undefined
    exportJourneyAsPDF(canvasRef, zoomLevel, setZoomLevel, journey.mapName || 'Journey Map');
  };

  const handleExportJSON = () => {
    exportJourneyAsJSON(stages, journey);
  };

  const handleImport = () => {
    importJourneyMap(setStages, setJourney);
  };

  const handleSave = () => {
    saveCurrentJourney(stages, journey);
  };

  const handleCreateNew = () => {
    // Save current journey before clearing
    handleSave();
    
    // Clear all current data
    handleClearAll();
    
    // Focus on the journey name field after a short delay to ensure the DOM has updated
    setTimeout(() => {
      const journeyNameInput = document.querySelector('input[name="mapName"]') as HTMLInputElement;
      if (journeyNameInput) {
        journeyNameInput.focus();
      }
    }, 100);
  };

  const completionPercentage = calculateCompletionPercentage(stages, journey);

  return (
    <div className="flex flex-col min-h-screen">
      <Header 
        title="User Journey Map Notebook" 
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
        onExport={handleExport}
        onExportPDF={handleExportPDF}
        onExportJSON={handleExportJSON}
        onImport={handleImport}
        onSave={handleSave}
        onCreateNew={handleCreateNew}
        setStages={setStages}
        setJourney={setJourney}
        zoomLevel={zoomLevel}
        stages={stages}
        journey={journey}
        isMobile={isMobile}
      />
      
      <main 
        ref={mainRef}
        className={`flex-1 ${isMobile ? 'px-0 py-2' : 'p-4'} bg-secondary/30 dark:bg-background/30 ${isMobile ? 'overflow-x-hidden' : 'overflow-x-auto'} overflow-y-visible`}
      >
        <div 
          className={`flex flex-col transition-transform duration-200 ease-out gap-4 ${isMobile ? 'w-full' : 'min-w-full'}`}
          style={{ transform: isMobile ? 'none' : `scale(${zoomLevel})` }}
          ref={canvasRef}
        >
          <JourneyProfileSection 
            journey={journey}
            onUpdate={handleProfileUpdate}
            completionPercentage={completionPercentage}
          />
          
          <JourneyContent 
            stages={stages}
            onAddStage={handleAddStage}
            onEdit={setEditingStage}
            onDelete={handleDeleteStage}
            onEmotionChange={handleEmotionChange}
            onQuoteChange={handleQuoteChange}
            onTitleChange={handleTitleChange}
            onDescriptionChange={handleDescriptionChange}
            onPainPointsChange={handlePainPointsChange}
            onOpportunitiesChange={handleOpportunitiesChange}
            onContentUpdate={handleContentUpdate}
            isMobile={isMobile}
            className="journey-content"
          />
          
          <div ref={insightsSectionRef} className={`bg-card rounded-lg shadow-sm ${isMobile ? 'p-2' : 'p-4'} relative`}>
            <InsightsSection 
              gaps={journey.insights.gaps}
              opportunities={journey.insights.opportunities}
              insightItems={journey.insightItems || []}
              onUpdate={(insights) => {
                console.log('InsightsSection onUpdate:', insights);
                
                // Make sure to correctly handle the insightItems array
                if (insights.insightItems) {
                  console.log('Updating insightItems:', insights.insightItems);
                }
                
                // Use a temporary object to ensure we're passing everything correctly
                const updateData: Partial<UserJourney> = {
                  insights: {
                    gaps: insights.gaps,
                    opportunities: insights.opportunities
                  }
                };
                
                // Only add insightItems if they exist
                if (insights.insightItems) {
                  updateData.insightItems = insights.insightItems;
                }
                
                handleProfileUpdate(updateData);
              }}
            />
            
            {/* Simple spacer for controls when at bottom */}
            {isAtBottom && !editingStage && !isInputFocused && (
              <div className="h-14 w-full mt-1" aria-hidden="true" />
            )}
          </div>
        </div>
      </main>
      
      {/* Show CanvasControls conditionally - either floating or positioned at the end of content */}
      <div 
        className={`canvas-controls-container transition-opacity duration-300 z-[150] ${
          !editingStage && !isScrolling && !isInputFocused ? 
            (isAtBottom ? 'opacity-100 static' : isStagesVisible ? 'opacity-100 fixed' : 'opacity-0 pointer-events-none') : 
            'opacity-0 pointer-events-none'
        }`}
      >
        <CanvasControls 
          onAddStage={handleAddStage}
          onClearAll={handleClearAll}
        />
      </div>
      {editingStage && (
        <StageEditor 
          stage={editingStage}
          onClose={() => setEditingStage(null)}
          onUpdate={handleUpdateStage}
        />
      )}
    </div>
  );
};

export default JourneyMap;
